import {
    START_SEARCH_REQUEST,
    START_SEARCH_SUCCESS,
    START_SEARCH_FAILURE,
    FETCH_SEARCH_REQUEST,
    FETCH_SEARCH_SUCCESS,
    FETCH_SEARCH_FAILURE,
    PING_SEARCH_REQUEST,
    PING_SEARCH_SUCCESS,
    PING_SEARCH_FAILURE,
    CANCEL_SEARCH_REQUEST,
    CANCEL_SEARCH_SUCCESS,
    CANCEL_SEARCH_FAILURE,
    FETCH_SEARCHES_REQUEST,
    FETCH_SEARCHES_SUCCESS,
    FETCH_SEARCHES_FAILURE,
    EXPORT_SEARCH_REQUEST,
    EXPORT_SEARCH_SUCCESS,
    EXPORT_SEARCH_FAILURE,
} from './Types';

import axios, { CancelTokenSource } from 'axios';
import _, { debounce } from 'lodash';
import { MAIN_MAP } from '../map/Constants';
import { addDrawFeature, setFitBoundsFromGeojson } from '../map/Actions';
import { setAreaCriteria, setAreaCriteriaAdvancedMode, setAreaLayer, clearArea } from '../search_area/Actions';
import { SEARCH_REDUCER } from '../ReducerNames';
import { SearchArea } from './searchTypes';
import { Criteria, Layer } from '../../utility/Types/commonTypes';

const CancelToken = axios.CancelToken;
let startSearchSource:CancelTokenSource|null, fetchSearchSource:CancelTokenSource|null, pingSearchSource:CancelTokenSource|null;

const startSearch = () => ({ type: START_SEARCH_REQUEST });
const startSearchSuccess = (search:SearchArea) => ({ type: START_SEARCH_SUCCESS, search });
const startSearchError = (error:string) => ({ type: START_SEARCH_FAILURE, error });
const fetchSearch = (id?:string) => ({ type: FETCH_SEARCH_REQUEST, id });
export const fetchSearchSuccess = (search:SearchArea) => ({ type: FETCH_SEARCH_SUCCESS, search });
const fetchSearchError = (id:string, error?:string) => ({ type: FETCH_SEARCH_FAILURE, id, error });
const pingSearch = () => ({ type: PING_SEARCH_REQUEST });
const pingSearchSuccess = (search:SearchArea) => ({ type: PING_SEARCH_SUCCESS, search });
const pingSearchError = (error:string) => ({ type: PING_SEARCH_FAILURE, error });
const cancelSearch = () => ({ type: CANCEL_SEARCH_REQUEST });
const cancelSearchSuccess = () => ({ type: CANCEL_SEARCH_SUCCESS });
const cancelSearchError = (error:string) => ({ type: CANCEL_SEARCH_FAILURE, error });
const fetchSearches = () => ({ type: FETCH_SEARCHES_REQUEST });
const fetchSearchesSuccess = (searches:SearchArea[]) => ({ type: FETCH_SEARCHES_SUCCESS, searches });
const fetchSearchesError = (error:string) => ({ type: FETCH_SEARCHES_FAILURE, error });
const exportSearch = () => ({ type: EXPORT_SEARCH_REQUEST });
const exportSearchSuccess = () => ({ type: EXPORT_SEARCH_SUCCESS });
const exportSearchError = (error: any) => ({ type: EXPORT_SEARCH_FAILURE, error });

const ping = (dispatch:(dispatch: any) => Promise<void>, id:string) => {
    pingSearchSource && pingSearchSource.cancel('Cancelling already running request');
    dispatch(pingSearch());
    return axios.get(`/api/searches/${id}`, {
        cancelToken: (pingSearchSource = axios.CancelToken.source()).token
    }).then(response => {
        pingSearchSource = null;
        dispatch(pingSearchSuccess(response.data));
        // If still running then ping again
        if (response.data.status === 'ready' || response.data.status === 'queued' || response.data.status === 'running') {
            debouncedPing(dispatch, response.data.id);
        }
    }).catch(error => {
        pingSearchSource = null;
        if (axios.isCancel(error)) {
            console.log('cancel', error);
        } else {
            dispatch(pingSearchError(error.response));
            console.error(error);
        }
    });
};

const debouncedPing = debounce(ping, 3000);

export const setSearchAreaCriteriaAdvancedMode = (isAdvanced: boolean) => {
    return (dispatch:(dispatch: any) => Promise<void>) => {
        dispatch(setAreaCriteriaAdvancedMode(SEARCH_REDUCER, isAdvanced));
    };
};

export const setSearchAreaCriteria = (criteria:Criteria) => {
    return (dispatch:(dispatch: any) => Promise<void>) => {
        dispatch(setAreaCriteria(SEARCH_REDUCER, criteria));
    };
};

export const setSearchSource = (id:number) => {
    return (dispatch:(dispatch: any) => Promise<void>) => {
        dispatch(setAreaLayer(SEARCH_REDUCER, id));
    };
};

export const clearSearch = () => {
    return (dispatch:(dispatch: any) => Promise<void>) => {
        dispatch(clearArea(SEARCH_REDUCER));
    };
};

export const startSearchAsync = (projectId:string, layer:Layer, areaFacetId:string, areaFacetValue:any, geometry:any, criteria:Criteria) => {
    return (dispatch:(dispatch: any) => Promise<void>) => {
        cancelRequests();

        dispatch(startSearch());
        return axios.post(`/api/projects/${projectId}/searches`, {
            layer: layer,
            area_facet_id: areaFacetId,
            area_facet_value: areaFacetValue,
            geometry: geometry,
            criteria: criteria
        }, {
            cancelToken: (startSearchSource = axios.CancelToken.source()).token
        }).then(response => {
            dispatch(startSearchSuccess(response.data));
            debouncedPing(dispatch, response.data.id);
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('cancel');
            } else {
                dispatch(startSearchError(error.response));
                console.error(error);
            }
        });
    };
};

// This function assumes that once the search loads the user wants to go to and show on the map
export const fetchSearchAsync = (id:string) => {
    return (dispatch:(dispatch: any) => Promise<void>) => {
        cancelRequests();

        dispatch(fetchSearch());
        return axios.get(`/api/searches/${id}`, {
            cancelToken: (fetchSearchSource = axios.CancelToken.source()).token
        }).then(response => {
            if (response.data.areaFacet.layerType === 'drawn') { dispatch(addDrawFeature(_.cloneDeep(response.data.area), MAIN_MAP)); }
            if (response.data.bounds) { dispatch(setFitBoundsFromGeojson(response.data.bounds, { maxZoom: 16 }, MAIN_MAP)); }
            dispatch(fetchSearchSuccess(response.data));
            if (['ready', 'queued', 'running'].includes(response.data.status)) {
                debouncedPing(dispatch, response.data.id);
            }
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('cancel');
            } else {
                dispatch(fetchSearchError(error.response));
                console.error(error);
            }
        });
    };
};

export const cancelSearchAsync = (id:string) => {
    return (dispatch:(dispatch: any) => Promise<void>) => {
        cancelRequests();
        dispatch(cancelSearch());
        return axios.patch(`/api/searches/${id}/cancel`, {

        }).then(response => {
            dispatch(cancelSearchSuccess());
        }).catch(error => {
            dispatch(cancelSearchError(error.response));
            console.error(error.response);
        });
    };
};

const cancelRequests = () => {
    debouncedPing.cancel();
    pingSearchSource && pingSearchSource.cancel('Operation cancelled by user');
    startSearchSource && startSearchSource.cancel('Cancelling already running request');
    fetchSearchSource && fetchSearchSource.cancel('Cancelling already running request');
};

export const fetchSearchesAsync = (projectId:string) => {
    return (dispatch:(dispatch: any) => Promise<void>) => {
        dispatch(fetchSearches());
        return axios.get(`/api/projects/${projectId}/searches`, {

        }).then(response => {
            dispatch(fetchSearchesSuccess(response.data));
        }).catch(error => {
            dispatch(fetchSearchesError(error.response));
            console.error(error.response);
        });
    };
};


export const exportSearchAsync = (id: number) => {
    return (dispatch: (arg0: { type: string; error?: any; }) => void) => {
        dispatch(exportSearch());
        return axios.post(`/api/searches/${id}/export`, {

        }).then(response => {
            // Download Begin
            // Apparently this is a standard way to download a file directly, whether this should be done from Redux? Probably not
            // "Proper" way would be to use standard href to download but also add on click functionality that routinely checks request
            // to see if it has finished
            const filenameRegex = /filename="?([^"]+)"?/;
            const filename = response.headers['content-disposition'].match(filenameRegex)[1];
            const element = document.createElement('a');
            element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(response.data));
            element.setAttribute('download', filename);
            element.style.display = 'none';
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            // Download End
            dispatch(exportSearchSuccess());
        }).catch(error => {
            dispatch(exportSearchError(error.response));
            console.error(error.response);
        });
    };
};

